import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { dataverseClient, TABLES } from '@shared/api';
import { escapeODataString } from '@shared/api/dataverse/utils';
import { assertGuid } from '@shared/lib';
import type { OpsGroup, OpsGroupQueryParams } from '../model/types';

// =============================================================================
// Entity Interface
// =============================================================================

interface OpsGroupEntity {
  vba_opsgroupid: string;
  vba_name: string;
  _vba_account_value?: string;
  vba_accountnumber?: string;
  vba_city?: string;
  vba_state?: string;
  vba_enrolledon?: string;
  _vba_enrolledby_value?: string;
  _ownerid_value?: string;
  vba_isactive?: boolean;
  vba_notes?: string;
  vba_contracturl?: string;
  createdon?: string;
  modifiedon?: string;
  // Expanded Account fields (group effective/term dates)
  vba_Account?: {
    dts_effectivedate?: string;
    dts_termdate?: string;
  };
  [key: string]: unknown;
}

// =============================================================================
// Mappers
// =============================================================================

function mapToDomain(entity: OpsGroupEntity): OpsGroup {
  return {
    id: entity.vba_opsgroupid,
    name: entity.vba_name ?? '',
    accountId: entity._vba_account_value ?? '',
    accountName: (entity['_vba_account_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    accountNumber: entity.vba_accountnumber ?? undefined,
    city: entity.vba_city ?? undefined,
    state: entity.vba_state ?? undefined,
    enrolledOn: entity.vba_enrolledon ?? undefined,
    enrolledById: entity._vba_enrolledby_value ?? undefined,
    enrolledByName: (entity['_vba_enrolledby_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    isActive: entity.vba_isactive ?? true,
    notes: entity.vba_notes ?? undefined,
    contractUrl: entity.vba_contracturl ?? undefined,
    ownerName: (entity['_ownerid_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    effectiveDate: entity.vba_Account?.dts_effectivedate ?? undefined,
    termDate: entity.vba_Account?.dts_termdate ?? undefined,
    createdOn: entity.createdon ?? undefined,
    modifiedOn: entity.modifiedon ?? undefined,
  };
}

function mapToEntity(input: Partial<OpsGroup>, accountId?: string): Record<string, unknown> {
  const entity: Record<string, unknown> = {};

  if (input.name !== undefined) entity.vba_name = input.name;
  if (input.accountNumber !== undefined) entity.vba_accountnumber = input.accountNumber;
  if (input.city !== undefined) entity.vba_city = input.city;
  if (input.state !== undefined) entity.vba_state = input.state;
  if (input.isActive !== undefined) entity.vba_isactive = input.isActive;
  if (input.notes !== undefined) entity.vba_notes = input.notes;
  if (input.contractUrl !== undefined) entity.vba_contracturl = input.contractUrl;

  const acctId = accountId ?? input.accountId;
  if (acctId) entity['vba_Account@odata.bind'] = `/accounts(${assertGuid(acctId, 'accountId')})`;

  return entity;
}

// =============================================================================
// API Functions
// =============================================================================

async function list(params: OpsGroupQueryParams = {}): Promise<OpsGroup[]> {
  const filters: string[] = [];
  if (params.activeOnly) filters.push('vba_isactive eq true');
  if (params.nameFilter) {
    filters.push(`contains(vba_name, '${escapeODataString(params.nameFilter)}')`);
  }

  const response = await dataverseClient.list<OpsGroupEntity>(TABLES.OPS_GROUP, {
    filter: filters.length > 0 ? filters.join(' and ') : undefined,
    orderBy: 'vba_name asc',
    expand: EXPAND_ACCOUNT,
  });

  return response.value.map(mapToDomain);
}

const EXPAND_ACCOUNT = ['vba_Account($select=dts_effectivedate,dts_termdate)'];

async function get(id: string): Promise<OpsGroup> {
  const entity = await dataverseClient.get<OpsGroupEntity>(TABLES.OPS_GROUP, id, {
    expand: EXPAND_ACCOUNT,
  });
  return mapToDomain(entity);
}

async function getByAccountId(accountId: string): Promise<OpsGroup | null> {
  const response = await dataverseClient.list<OpsGroupEntity>(TABLES.OPS_GROUP, {
    filter: `_vba_account_value eq '${assertGuid(accountId, 'accountId')}'`,
    top: 1,
    expand: EXPAND_ACCOUNT,
  });
  return response.value.length > 0 ? mapToDomain(response.value[0]) : null;
}

async function create(input: Partial<OpsGroup>): Promise<OpsGroup> {
  const entity = mapToEntity(input, input.accountId);
  // Stamp enrollment date
  entity.vba_enrolledon = new Date().toISOString().split('T')[0];
  const response = await dataverseClient.create<OpsGroupEntity>(TABLES.OPS_GROUP, entity);
  return mapToDomain(response);
}

async function update(id: string, input: Partial<OpsGroup>): Promise<void> {
  const entity = mapToEntity(input);
  await dataverseClient.update(TABLES.OPS_GROUP, id, entity);
}

async function remove(id: string): Promise<void> {
  await dataverseClient.remove(TABLES.OPS_GROUP, id);
}

// =============================================================================
// React Query Hooks
// =============================================================================

const keys = {
  all: ['opsGroups'] as const,
  lists: () => [...keys.all, 'list'] as const,
  list: (params: OpsGroupQueryParams) => [...keys.lists(), params] as const,
  detail: (id: string) => [...keys.all, 'detail', id] as const,
  byAccount: (accountId: string) => [...keys.all, 'byAccount', accountId] as const,
};

export function useOpsGroups(
  params: OpsGroupQueryParams = {},
  options?: Omit<UseQueryOptions<OpsGroup[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: keys.list(params),
    queryFn: () => list(params),
    ...options,
  });
}

export function useOpsGroup(id: string) {
  return useQuery({
    queryKey: keys.detail(id),
    queryFn: () => get(id),
    enabled: !!id,
  });
}

export function useOpsGroupByAccountId(accountId: string) {
  return useQuery({
    queryKey: keys.byAccount(accountId),
    queryFn: () => getByAccountId(accountId),
    enabled: !!accountId,
  });
}

export function useCreateOpsGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: create,
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); },
  });
}

export function useUpdateOpsGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<OpsGroup> }) => update(id, input),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); },
  });
}

export function useDeleteOpsGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: remove,
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); },
  });
}
